import Cookies from "js-cookie";
import {COUNTRIES_TO_DOMAINS} from "../constants/locale";

const MIN_PAD_LEFT_RIGHT = 25;

/**
 * helper method to compute the position of the
 * desktop nav dropdown menu
 * @param {number} windowWidth
 * @param {HTMLDivElement} menuContainer
 * @param {HTMLDivElement} dropdownMenu
 * @return {Record<string, string>}
 */
export const computeMenuPosition = (
  windowWidth: number,
  menuContainer: HTMLDivElement,
  dropdownMenu: HTMLDivElement
): Record<string, string> => {
  let right = "auto";
  let left = "auto";

  // set position of the menu wrapper, to make sure that it's not overflowing
  const wrapperBRect = dropdownMenu.getBoundingClientRect();
  const wrapperComputedStyle = getComputedStyle(dropdownMenu);

  const wrapperPadLeft =
    parseFloat(wrapperComputedStyle.paddingLeft) +
    parseFloat(wrapperComputedStyle.marginLeft);
  const wrapperPadRight =
    parseFloat(wrapperComputedStyle.paddingRight) +
    parseFloat(wrapperComputedStyle.marginRight);

  const wrapperWidth = wrapperBRect.width;

  const menuWidth = menuContainer.getBoundingClientRect().width;

  const wrapperWidthWOPads = wrapperWidth - wrapperPadLeft - wrapperPadRight;
  const dropDownBtnLeftPos = wrapperBRect.left;

  // since the component wrapper has position relative, it means that
  // the position of the menu content is always relative to the wrapper.
  // the ideal position of the menu content should be centered
  const idealLeftPos = -((menuWidth - wrapperWidthWOPads) / 2) + wrapperPadLeft;
  const idealMaxMenuWidth = windowWidth - MIN_PAD_LEFT_RIGHT * 2;

  // - if menu mid focal + (button position + padleft) exceed width, snap to the right
  // - if menu width >= window width or wrapper left pos + menu width > ideal max menu width,
  // stick it to left of the main window
  // - if the wrapper position + ideal post < 0, snap it to left
  // - otherwise, use ideal left position computed value
  if (menuWidth / 2 + dropDownBtnLeftPos + wrapperPadLeft > windowWidth) {
    right = "0px";
    left = "auto";
  } else if (
    menuWidth >= idealMaxMenuWidth ||
    dropDownBtnLeftPos + menuWidth >= idealMaxMenuWidth
  ) {
    left = `${Math.floor(
      -dropDownBtnLeftPos + (windowWidth - menuWidth) / 2
    )}px`;
    right = "auto";
  } else if (dropDownBtnLeftPos + idealLeftPos < 0) {
    right = "auto";
    left = "0px";
  } else {
    left = `${Math.floor(idealLeftPos)}px`;
    right = "auto";
  }

  return {right, left};
};

/* When adding country codes, please ensure they match exactly
 * the relevant code specified here:
 * https://dev.maxmind.com/geoip/legacy/codes/iso3166/
 * This is required by Netlify's GeoIP redirect functionality:
 * https://www.netlify.com/docs/redirects/#geoip-and-language-based-redirects
 */

export enum NetlifyCountry {
  US = "US",
  CN = "CN",
}

export const LANGUAGE_COOKIE_NAME = "nf_country";

export interface LocaleOption {
  label: string;
  country: NetlifyCountry;
}

/**
 * helper method to handle lang change
 * @param {NetlifyCountry} country
 * @return {void}
 */
export const handleLangChange = (country: NetlifyCountry) => {
  Cookies.set(LANGUAGE_COOKIE_NAME, country, {
    expires: 365,
    domain: "flexport.com",
  });
  const newDomain = COUNTRIES_TO_DOMAINS[country];
  const newUrl = window.location.href.replace(
    window.location.origin,
    newDomain
  );
  window.location.assign(newUrl);
};

/**
 * helper method to grab focusable element under html element
 * @param {HTMLElement} element
 * @return {Array<Element>}
 */
export const getKeyboardFocusableElements = (
  element: HTMLElement
): Array<Element> => {
  const temp: Array<Element> = [
    ...element.querySelectorAll(
      "a[href], button, input, textarea, select, details,[tabindex]:not([tabindex='-1'])"
    ),
  ];

  return temp.filter(
    el => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden")
  );
};
